import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown, AlertTriangle, Calendar, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getPlayers, getWellness, saveWellness, saveRPE } from '../api';

const MUSCLE_LOCATIONS = [
  'Full Body','Full Upper Body','Full Lower Body',
  'Neck','Shoulders','Back','Chest','Abdomen',
  'Arms','Groin','Glutes','Quadriceps','Hamstrings','Calves'
];
const PAIN_LOCATIONS = [
  ...MUSCLE_LOCATIONS,
  'Head','Elbow','Wrist','Hand','Finger','Hip','Knee','Ankle','Toe'
];

const today = () => new Date().toISOString().split('T')[0];
const IS_STAFF = true;

const emptyForm = {
  date: today(), playerId: '',
  sleepHoursNum: '', sleepMinsNum: '',
  sleepQuality: '', energyLevel: '',
  musclesoreness: '', sorenessLocation: [],
  stressLevel: '', otherPain: false, painLevel: '', painLocation: [],
  rpeMuscular: '', rpeCardiovascular: '',
};

const fmtSleepHours = h => {
  if (h == null || h === '') return null;
  const hrs  = Math.floor(Number(h));
  const mins = Math.round((Number(h) % 1) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};

function getScoreColor(val, inverse = false) {
  if (!val) return 'var(--text-muted)';
  const pct = val / 10;
  if (inverse) return pct >= 0.7 ? '#ef4444' : pct >= 0.4 ? '#f59e0b' : '#10b981';
  return pct >= 0.7 ? '#10b981' : pct >= 0.4 ? '#f59e0b' : '#ef4444';
}

export const rpeValueColor = v => {
  if (!v) return '#3b82f6';
  if (v <= 3)  return '#60a5fa'; // light blue
  if (v <= 6)  return '#10b981'; // green
  if (v === 7) return '#facc15'; // yellow
  if (v === 8) return '#f97316'; // orange
  if (v === 9) return '#ef4444'; // red
  return '#991b1b';              // dark red (10)
};
export const rpeAssessment = v => {
  if (!v) return '';
  if (v <= 2) return 'Very light';
  if (v <= 4) return 'Light';
  if (v <= 6) return 'Moderate';
  if (v <= 8) return 'Hard';
  return 'Max';
};

function EfficiencyInput({ value, onChange }) {
  const num = value === '' ? '' : Number(value);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <input
          type="range" min={0} max={100} step={1}
          value={num === '' ? 50 : num}
          onChange={e => onChange(Number(e.target.value))}
          style={{ flex: 1, accentColor: '#8b5cf6', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <input
            type="number" min={0} max={100}
            value={value}
            onChange={e => {
              const raw = e.target.value;
              if (raw === '') { onChange(''); return; }
              const v = Math.min(100, Math.max(0, Number(raw)));
              onChange(isNaN(v) ? '' : v);
            }}
            style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, textAlign: 'center', outline: 'none' }}
          />
          <span style={{ fontSize: 15, color: 'var(--text-muted)', fontWeight: 600 }}>%</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)' }}>
        <span>0% — Poor</span><span>100% — Perfect</span>
      </div>
    </div>
  );
}

function ScaleSelector({ value, onChange, min, max, lowLabel, highLabel, color = '#3b82f6', colorFn }) {
  const nums = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {nums.map(n => {
          const btnColor = colorFn ? colorFn(n) : color;
          const isYellow = btnColor === '#facc15';
          return (
            <button key={n} onClick={() => onChange(n)}
              style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid', borderColor: value === n ? btnColor : 'var(--border-strong)', background: value === n ? btnColor : 'var(--bg-app)', color: value === n ? (isYellow ? '#111' : '#fff') : 'var(--text-secondary)', fontWeight: value === n ? 700 : 400, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s' }}>
              {n}
            </button>
          );
        })}
      </div>
      {(lowLabel || highLabel) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>{lowLabel}</span><span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const toggle = opt => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: value.length ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 14, textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{value.length ? value.join(', ') : placeholder}</span>
        <ChevronDown size={16} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, zIndex: 50, maxHeight: 220, overflowY: 'auto' }}>
          {options.map(opt => (
            <div key={opt} onClick={() => toggle(opt)}
              style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, background: value.includes(opt) ? '#1e3a5f' : 'transparent', color: value.includes(opt) ? '#93c5fd' : 'var(--text-primary)' }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: '1px solid', borderColor: value.includes(opt) ? '#3b82f6' : 'var(--text-dimmed)', background: value.includes(opt) ? '#3b82f6' : 'transparent', flexShrink: 0 }} />
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{number}</div>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, sublabel, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: sublabel ? 2 : 8 }}>{label}</label>
      {sublabel && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{sublabel}</p>}
      {children}
    </div>
  );
}

export default function WellnessInput({ playerOverride = null, defaultTab = 'wellness', sessionType = 'pitch', hideTabs = [] }) {
  const isGym = sessionType === 'gym';
  const [players, setPlayers]       = useState([]);
  const [form, setForm]             = useState(emptyForm);
  const [submitted, setSubmitted]   = useState(false);
  const [activeTab, setActiveTab]   = useState(defaultTab);
  const [duplicate, setDuplicate]   = useState(false);
  const [histPlayer, setHistPlayer] = useState('');
  const [wellnessData, setWellnessData] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, w] = await Promise.all([getPlayers(), getWellness()]);
        setPlayers(p);
        setWellnessData(w.map(normalizeEntry));
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const normalizeEntry = e => {
    const sh = e.sleep_hours;
    return ({
    ...e,
    playerId: String(e.player_id),
    date: e.date ? e.date.slice(0, 10) : '',
    sleepHours: sh,
    sleepHoursNum: sh != null ? Math.floor(sh) : '',
    sleepMinsNum:  sh != null ? Math.round((sh % 1) * 60) : '',
    sleepQuality: e.sleep_quality,
    energyLevel: e.energy_level,
    musclesoreness: e.muscle_soreness,
    sorenessLocation: typeof e.soreness_location === 'string' ? JSON.parse(e.soreness_location || '[]') : (e.soreness_location || []),
    stressLevel: e.stress_level,
    otherPain: e.other_pain,
    painLevel: e.pain_level,
    painLocation: typeof e.pain_location === 'string' ? JSON.parse(e.pain_location || '[]') : (e.pain_location || []),
    submittedAt: e.submitted_at,
  });
  };

  useEffect(() => {
    if (!form.playerId || !form.date) { setDuplicate(false); return; }
    const found = wellnessData.find(e => e.playerId === String(form.playerId) && e.date === form.date);
    if (found) {
      setDuplicate(true);
      setForm(f => ({ ...found, playerId: f.playerId, date: f.date }));
    } else {
      setDuplicate(false);
      setForm(f => ({ ...emptyForm, playerId: f.playerId, date: f.date }));
    }
  }, [form.playerId, form.date]);

  const overridePlayerId = playerOverride?.id;
  useEffect(() => {
    if (overridePlayerId) setForm(f => ({ ...f, playerId: String(overridePlayerId) }));
  }, [overridePlayerId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.playerId) { alert('Please select a player first.'); return; }
    if (duplicate && !IS_STAFF) return;
    try {
      if (activeTab === 'rpe') {
        if (!form.rpeMuscular) { alert('Please rate Muscular Exertion.'); return; }
        if (!isGym && !form.rpeCardiovascular) { alert('Please rate both Muscular and Cardiovascular Exertion.'); return; }
        await saveRPE({
          player_id: parseInt(form.playerId),
          date: form.date || today(),
          rpe_muscular: form.rpeMuscular,
          rpe_cardiovascular: isGym ? null : form.rpeCardiovascular,
          session_type: sessionType,
        });
      } else {
        const saved = await saveWellness({
          player_id: parseInt(form.playerId),
          date: form.date,
          sleep_hours: (form.sleepHoursNum !== '' || form.sleepMinsNum !== '')
            ? Number(form.sleepHoursNum || 0) + Number(form.sleepMinsNum || 0) / 60
            : null,
          sleep_quality: form.sleepQuality || null,
          energy_level: form.energyLevel || null,
          muscle_soreness: form.musclesoreness || null,
          soreness_location: form.sorenessLocation || [],
          stress_level: form.stressLevel || null,
          other_pain: form.otherPain || false,
          pain_level: form.painLevel || null,
          pain_location: form.painLocation || [],
        });
        const normalized = normalizeEntry(saved);
        setWellnessData(prev => {
          const exists = prev.find(e => e.playerId === String(form.playerId) && e.date === form.date);
          return exists
            ? prev.map(e => e.playerId === String(form.playerId) && e.date === form.date ? normalized : e)
            : [...prev, normalized];
        });
      }
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setForm(emptyForm); setDuplicate(false); }, 2500);
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save. Please try again.');
    }
  };

  const playerHistory = () => {
    if (!histPlayer) return [];
    return wellnessData
      .filter(e => e.playerId === String(histPlayer))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 14);
  };

  const chartData = () => playerHistory().slice(0, 7).reverse().map(e => ({
    date: e.date ? e.date.slice(5) : '',
    'Stress':   e.stressLevel    || null,
    'Soreness': e.musclesoreness || null,
  }));

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>Loading...</div>;

  if (submitted) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
      <CheckCircle size={64} color="#10b981" strokeWidth={1.5} />
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{duplicate ? 'Entry updated!' : 'Submitted successfully!'}</h2>
      <p style={{ color: 'var(--text-muted)' }}>Thank you. Your response has been saved.</p>
    </div>
  );

  const tabs = [
    { id: 'wellness', label: '🌡️ Wellness Check-in' },
    { id: 'rpe',      label: isGym ? '🏋️ Gym RPE' : '⚽ Pitch RPE' },
    { id: 'history',  label: '📅 My History' },
  ].filter(t => !hideTabs.includes(t.id));

  const rpeRef = [['1–2','Very light'],['3–4','Light'],['5–6','Moderate'],['7–8','Hard'],['9–10','Max']];

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Daily Monitoring</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Complete your wellness check-in and session RPE below.</p>
      </div>

      <div style={{ display: 'flex', gap: 4, background: 'var(--border)', borderRadius: 10, padding: 4, marginBottom: 24, border: '1px solid var(--border-strong)' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: activeTab === tab.id ? '#3b82f6' : 'transparent', color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)', fontWeight: activeTab === tab.id ? 600 : 400, fontSize: 14 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Select Player</label>
            <select value={histPlayer} onChange={e => setHistPlayer(e.target.value)} style={inputStyle}>
              <option value="">Select player...</option>
              {players.filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {histPlayer && chartData().length > 0 && (
            <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>7-Day Trends</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData()}>
                  <XAxis dataKey="date" stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                  <YAxis domain={[0, 10]} stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8 }} labelStyle={{ color: 'var(--text-primary)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Stress"   stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="Soreness" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {histPlayer && playerHistory().length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No submissions found for this player yet.</div>
          )}
          {playerHistory().map(e => (
            <div key={e.id} style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={16} color="var(--text-muted)" />
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{e.date}</span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.submittedAt ? new Date(e.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {[
                  { label: 'Sleep',       val: fmtSleepHours(e.sleepHours), unit: '', color: '#3b82f6' },
                  { label: 'Efficiency',  val: e.sleepQuality,   unit: '%',   color: e.sleepQuality >= 80 ? '#10b981' : e.sleepQuality >= 60 ? '#f59e0b' : '#ef4444' },
                  { label: 'HRV',         val: e.energyLevel,    unit: ' ms', color: e.energyLevel >= 40 ? '#10b981' : e.energyLevel >= 25 ? '#f59e0b' : '#ef4444' },
                  { label: 'Soreness',    val: e.musclesoreness, unit: '/10', color: getScoreColor(e.musclesoreness, true) },
                  { label: 'Stress',      val: e.stressLevel,    unit: '/10', color: getScoreColor(e.stressLevel, true) },
                  { label: 'Pain',        val: e.otherPain ? (e.painLevel || 'Yes') : 'No', unit: '', color: e.otherPain ? '#ef4444' : '#10b981' },
                ].map(({ label, val, unit, color }) => (
                  <div key={label} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color }}>{val !== '' && val !== undefined && val !== null ? `${val}${unit}` : '—'}</div>
                  </div>
                ))}
              </div>
              {e.sorenessLocation?.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Soreness: <span style={{ color: '#f59e0b' }}>{e.sorenessLocation.join(', ')}</span></div>
              )}
              {e.otherPain && e.painLocation?.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>Pain: <span style={{ color: '#ef4444' }}>{e.painLocation.join(', ')}</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Player + date selector */}
      {activeTab !== 'history' && (
        <>
          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {!playerOverride ? (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Player</label>
                <select value={form.playerId} onChange={e => set('playerId', e.target.value)} style={inputStyle}>
                  <option value="">Select player...</option>
                  {players.filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                    <option key={p.id} value={p.id}>{p.name} {p.squad_number ? `(#${p.squad_number})` : ''}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Player</label>
                <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                  {playerOverride.name}
                </div>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inputStyle} />
            </div>
          </div>

          {duplicate && activeTab === 'wellness' && (
            <div style={{ background: '#451a03', border: '1px solid #92400e', borderRadius: 10, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontWeight: 600, color: '#fbbf24', marginBottom: 4 }}>Already submitted today</p>
                <p style={{ fontSize: 13, color: '#d97706' }}>
                  {IS_STAFF ? 'An entry already exists. Submitting will overwrite it.' : 'You have already submitted today.'}
                </p>
                {IS_STAFF && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#f59e0b' }}>
                    <RefreshCw size={14} /> Submitting will overwrite the existing entry.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Wellness Form */}
      {activeTab === 'wellness' && (!duplicate || IS_STAFF) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section number={1} title="Sleep Duration">
            <Field label="How long did you sleep last night?">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number" min={0} max={24} value={form.sleepHoursNum}
                  onChange={e => set('sleepHoursNum', e.target.value)}
                  style={{ ...inputStyle, width: 90 }} placeholder="0"
                />
                <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>h</span>
                <input
                  type="number" min={0} max={59} value={form.sleepMinsNum}
                  onChange={e => set('sleepMinsNum', e.target.value)}
                  style={{ ...inputStyle, width: 90 }} placeholder="00"
                />
                <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>min</span>
              </div>
            </Field>
          </Section>
          <Section number={2} title="Sleep Efficiency">
            <Field label="What was your sleep efficiency?" sublabel="Check your Oura app — shown as a percentage">
              <EfficiencyInput value={form.sleepQuality} onChange={v => set('sleepQuality', v)} />
            </Field>
          </Section>
          <Section number={3} title="Muscle Soreness">
            <Field label="Do you experience any muscle soreness?" sublabel="Select all affected areas — leave empty if none">
              <MultiSelect options={MUSCLE_LOCATIONS} value={form.sorenessLocation} onChange={v => set('sorenessLocation', v)} placeholder="Select location(s)..." />
            </Field>
            {form.sorenessLocation.length > 0 && (
              <Field label="How would you rate the severity?" sublabel="1 = Barely noticeable · 10 = Severe">
                <ScaleSelector value={form.musclesoreness} onChange={v => set('musclesoreness', v)} min={1} max={10} lowLabel="Barely noticeable" highLabel="Severe" color="#f59e0b" />
              </Field>
            )}
          </Section>
          <Section number={4} title="Stress Level">
            <Field label="How would you rate your current stress level?" sublabel="1 = Completely relaxed · 10 = Highly stressed">
              <ScaleSelector value={form.stressLevel} onChange={v => set('stressLevel', v)} min={1} max={10} lowLabel="Completely relaxed" highLabel="Highly stressed" color="#ef4444" />
            </Field>
          </Section>
          <Section number={5} title="Pain & Discomfort">
            <Field label="Are you experiencing any other pain or physical discomfort?">
              <div style={{ display: 'flex', gap: 10 }}>
                {[true, false].map(v => (
                  <button key={String(v)} onClick={() => set('otherPain', v)}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid', borderColor: form.otherPain === v ? (v ? '#ef4444' : '#10b981') : 'var(--border-strong)', background: form.otherPain === v ? (v ? '#ef444422' : '#10b98122') : 'var(--bg-app)', color: form.otherPain === v ? (v ? '#ef4444' : '#10b981') : 'var(--text-secondary)', cursor: 'pointer', fontWeight: form.otherPain === v ? 700 : 400, fontSize: 14 }}>
                    {v ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </Field>
            {form.otherPain === true && (
              <>
                <Field label="Select the affected area(s)">
                  <MultiSelect options={PAIN_LOCATIONS} value={form.painLocation} onChange={v => set('painLocation', v)} placeholder="Select location(s)..." />
                </Field>
                <Field label="How would you rate the severity?" sublabel="1 = Mild discomfort · 10 = Severe pain">
                  <ScaleSelector value={form.painLevel} onChange={v => set('painLevel', v)} min={1} max={10} lowLabel="Mild discomfort" highLabel="Severe pain" color="#ef4444" />
                </Field>
              </>
            )}
          </Section>
        </div>
      )}

      {/* RPE Form */}
      {activeTab === 'rpe' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { num: 1, title: 'Muscular Exertion', key: 'rpeMuscular', color: '#f59e0b', label: 'How would you rate the muscular demand of this session?', sub: '1 = Very light · 10 = Maximum muscular exertion' },
            // Gym RPE is muscular-only (2026-08-17) — see MobileRPEWizard's STEPS_BY_TYPE.
            ...(isGym ? [] : [{ num: 2, title: 'Cardiovascular Exertion', key: 'rpeCardiovascular', color: '#3b82f6', label: 'How heavy did the session feel on your lungs and heart?', sub: '1 = Very light · 10 = Maximum cardiovascular exertion' }]),
          ].map(({ num, title, key, color, label, sub }) => (
            <Section key={key} number={num} title={title}>
              <Field label={label} sublabel={sub}>
                <ScaleSelector value={form[key]} onChange={v => set(key, v)} min={1} max={10} lowLabel="Very light" highLabel="Maximum exertion" colorFn={rpeValueColor} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginTop: 8 }}>
                {rpeRef.map(([range, lbl]) => (
                  <div key={range} style={{ background: 'var(--bg-app)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color }}>{range}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </Section>
          ))}
        </div>
      )}

      {activeTab !== 'history' && (!duplicate || IS_STAFF) && (
        <button onClick={handleSubmit}
          style={{ width: '100%', marginTop: 24, padding: '14px', borderRadius: 10, border: 'none', background: duplicate ? '#059669' : '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
          {duplicate ? '🔄 Update Entry' : `Submit ${activeTab === 'wellness' ? 'Wellness Check-in' : (isGym ? 'Gym RPE' : 'Pitch RPE')}`}
        </button>
      )}
    </div>
  );
}